import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector, useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { useCohortDefinitionStore } from '@verantos-dev/app-state-cohort-definition';
import {
  addResearchAssistantMessages,
  selectResearchAssistantModel,
} from '@verantos-dev/app-state-research-assistant';
import { useAuth } from '@verantos-dev/core-auth';
import {
  useAutosaveCohortMutation,
  useAutosaveDraftMutation,
  type AutosaveDraftResponse,
} from '@verantos-dev/data-access-cohorts';
import { avKeys, cohortDefinitionToQueryData } from '@verantos-dev/feature-analytical-variables';
import { useResearchAssistantSocketContext } from '@verantos-dev/feature-research-assistant';
import { CohortBuilderDefinitionLegacy, CohortDefinitionStatus } from '@verantos-dev/types-cohorts';
import { ResearchAssistantMessageEnumLegacy } from '@verantos-dev/types-common';

import { useSaveTimeoutNoticeStore } from '../../components/StagedChangesPill/useSaveTimeoutNotice';

/**
 * Returns a callable that triggers the cohort-builder autosave.
 *
 * Two transport paths live here, behind the `research_assistant_gen_3` flag:
 *
 * - **Gen-3 (ff on)** — `useAutosaveDraftMutation` issues a REST PUT to the
 *   draft endpoint. No RA socket is required, and the "I just edited the UI"
 *   system message is no longer pushed into chat (the assistant gets the
 *   user's active context via the context-envelope work — see OR-6101).
 * - **Legacy (ff off)** — `useAutosaveCohortMutation` does the original
 *   socket round-trip (`publish:uiGeneratedCohort` + `staged_changes_updated`)
 *   and dispatches the legacy info message into the RA chat. Behavior is
 *   intentionally unchanged so legacy callers see no regression.
 *
 * The callable signature is preserved across both branches — existing
 * consumers keep destructuring `useEmitResearchAssistantUpdate()` directly.
 *
 * TODO: cleanup with research_assistant_gen_3 ff (collapse to the REST
 * branch and drop the legacy socket path along with the RA chat dispatch).
 */
export const useEmitResearchAssistantUpdate = (viewMode?: boolean) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { projectId, cohortId, cohortVersionId } = useParams();
  const researchAssistantModel = useAppSelector(selectResearchAssistantModel);
  const { socket } = useResearchAssistantSocketContext();
  const isGen3 = useFeatureFlagSplit('research_assistant_gen_3', false);
  // TODO: cleanup with explicit_save ff
  const isExplicitSave = useFeatureFlagSplit('explicit_save');

  const socketMutation = useAutosaveCohortMutation();
  const draftMutation = useAutosaveDraftMutation();

  // Tracks the AbortController for the current in-flight gen-3 autosave PUT.
  // Aborting before starting a new one implements cancel-and-replace, preventing
  // concurrent PUTs with interleaved or stale payloads (OR-6118).
  const autosaveAbortRef = useRef<AbortController | null>(null);

  // Ref to access latest values in callbacks without triggering re-renders
  const depsRef = useRef({
    projectId,
    cohortId,
    cohortVersionId,
    researchAssistantModel,
    user,
    socket,
  });

  useEffect(() => {
    depsRef.current = {
      projectId,
      cohortId,
      cohortVersionId,
      researchAssistantModel,
      user,
      socket,
    };
  }, [projectId, cohortId, cohortVersionId, researchAssistantModel, user, socket]);

  const { mutate: triggerSocketAutosave } = socketMutation;
  const { mutate: triggerDraftAutosave } = draftMutation;

  const emitResearchAssistantUpdate = useCallback(
    (cohortDefinition: CohortBuilderDefinitionLegacy) => {
      if (viewMode) {
        // viewMode: no persistence, no chat side effect.
        return;
      }

      const { projectId, cohortId, cohortVersionId, researchAssistantModel, user, socket } =
        depsRef.current;
      const store = useCohortDefinitionStore.getState();

      // OR-6158: a run auto-commits the draft server-side; suppress autosave
      // (and abort any in-flight PUT) so a late edit-emit can't re-stage it.
      if (store.profile.latestStatus === CohortDefinitionStatus.PROCESSING) {
        autosaveAbortRef.current?.abort();
        return;
      }

      if (isGen3) {
        // Cancel-and-replace: abort any in-flight autosave before starting a new
        // one. This prevents concurrent PUTs — the previous request was carrying
        // an older payload that is now superseded (OR-6118).
        autosaveAbortRef.current?.abort();
        autosaveAbortRef.current = new AbortController();

        // Clear any pending failure-pill state on a fresh write — REST gives
        // us a real lifecycle (no 8s timeout fake), so the failure pill flips
        // open on mutation error below and clears here on the retry path.
        useSaveTimeoutNoticeStore.getState().dismiss();

        // Seed the definition cache synchronously, BEFORE the async mutation, so
        // the update survives the calling component unmounting right after the
        // edit (the add-from-library modal closes; a criterion card is deleted).
        // React Query skips a mutate-level onSuccess once its observer has
        // unmounted, so seeding the cache there silently dropped adds/removes on
        // navigate-away-and-back even though the draft persisted server-side
        // (OR-6110). Navigating away and back within the 5-min staleTime is served
        // from this cache with no GET, so it must hold the edit. The payload IS
        // what we persist (same transform as a fetch); hasStagedChanges is
        // optimistically true — any definition-changing edit stages a draft — and
        // reconciled from the server's net-zero verdict in onSuccess below.
        const versionForCache =
          useCohortDefinitionStore.getState().profile?.latestCohortVersionId ?? cohortVersionId;
        if (versionForCache) {
          queryClient.setQueryData(
            avKeys.list(versionForCache),
            cohortDefinitionToQueryData(cohortDefinition, true)
          );
        }

        triggerDraftAutosave(
          {
            projectId: projectId || '',
            cohortId: cohortId || '',
            jsonValue: cohortDefinition,
            signal: autosaveAbortRef.current.signal,
          },
          {
            onSuccess: (response: AutosaveDraftResponse) => {
              // setIsSaving(false) is a defensive clear; isSaving is not set true
              // on the gen-3 path so buttons are never blocked by the round-trip.
              useCohortDefinitionStore.getState().setIsSaving(false);
              useCohortDefinitionStore.setState({ hasStagedChanges: !!response.hasStagedChanges });
              const next = useCohortDefinitionStore.getState();
              if (response.newStatus) {
                next.setProfileStatus(response.newStatus as CohortDefinitionStatus);
              }
              if (response.versionId) {
                useCohortDefinitionStore.setState((s) => ({
                  profile: { ...s.profile, latestCohortVersionId: response.versionId ?? undefined },
                }));
              }
              // The cache was already seeded synchronously above with
              // hasStagedChanges=true. Reconcile only when the server reports a
              // net-zero edit (definition back to committed → no chip). Best-effort:
              // this onSuccess is skipped if the calling component already
              // unmounted, which only affects the rare net-zero-from-a-transient-
              // control case; the socket `staged_changes_updated` event is the
              // store's authoritative chip source either way.
              if (!response.hasStagedChanges) {
                const reconcileVersion =
                  useCohortDefinitionStore.getState().profile?.latestCohortVersionId ??
                  cohortVersionId;
                if (reconcileVersion) {
                  queryClient.setQueryData(
                    avKeys.list(reconcileVersion),
                    cohortDefinitionToQueryData(cohortDefinition, false)
                  );
                }
              }
            },
            onError: (error) => {
              // Aborted requests are intentional (cancel-and-replace); a newer
              // autosave is already in-flight so there is nothing to surface.
              if (error.name === 'AbortError') return;
              useCohortDefinitionStore.getState().setIsSaving(false);
              // Surface the failure UX immediately — the caller's payload is
              // retained for the Retry button on the staged-changes pill.
              useSaveTimeoutNoticeStore.getState().openFailure(cohortDefinition);
            },
          }
        );
        return;
      }

      // Legacy socket path — unchanged from pre-OR-6101 behavior.
      // Set saving indicator only — let the BE decide hasStagedChanges via
      // the staged_changes_updated socket event (single source of truth).
      store.setIsSaving(true);
      triggerSocketAutosave({
        projectId: projectId || '',
        cohortId: cohortId || '',
        cohortVersionId: cohortVersionId || '',
        model: researchAssistantModel,
        cohortDefinition,
        user,
        socket: socket ?? null,
      });
      if (isExplicitSave) {
        useSaveTimeoutNoticeStore.getState().armTimeout(cohortDefinition);
      }

      // Legacy info message — gen-3 drops this; the assistant gets the
      // user's active context via the context envelope instead.
      dispatch(
        addResearchAssistantMessages([
          {
            message: intl.formatMessage({
              id: 'projects.cohortBuilderPage.researchAssistant.updateInfoMessage',
            }),
            timestamp: new Date().toISOString(),
            currentUser: 'user',
            isCurrentUser: true,
            type: ResearchAssistantMessageEnumLegacy.INFO,
          },
        ])
      );
    },
    [
      viewMode,
      isGen3,
      isExplicitSave,
      dispatch,
      intl,
      triggerSocketAutosave,
      triggerDraftAutosave,
      queryClient,
    ]
  );

  // Backwards-compatible callable. The attached `.mutation` is the active
  // transport — gen-3 callers read REST mutation state, legacy callers read
  // socket mutation state.
  return Object.assign(emitResearchAssistantUpdate, {
    mutation: isGen3 ? draftMutation : socketMutation,
  });
};
